import { React, useState } from 'react';

import { Grid, Paper, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { empresas } from '../../utils/empresas';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatDecimal, parseNumericText } from '../../utils/numberUtils';

const initialRows = empresas.map(({ id, empresa }) => ({
    id,
    empresa,
    valor: 0
}));

const LancamentoSPE = () => {
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [rows, setRows] = useState(initialRows);
    const [dateTimePeriodo, setDateTimePeriodo] = useState(null);
    const [errorPeriodo, setErrorPeriodo] = useState(false);
    const [valoresPaste, setValoresPaste] = useState('');
    const [pasteSummary, setPasteSummary] = useState(null);

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            width: 50,
            hide: true
        },
        {
            field: 'empresa',
            headerName: 'Empresas',
            width: 500,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'valor',
            headerName: 'Valor',
            width: 200,
            editable: true,
            valueFormatter: (value) => (value === null || value === undefined ? '' : formatDecimal(value)),
            align: 'center',
            headerAlign: 'center'
        }
    ];

    const processRowUpdate = (newRow, oldRow) => {
        const updatedRow = { ...newRow, valor: parseNumericText(newRow.valor, null) };
        setRows((prevRows) =>
            prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row))
        );
        return updatedRow;
    };

    function parseValoresPaste (text) {
        const values = text
            .replace(/(\r?\n)+$/, '')
            .split(/\r?\n|\t/)
            .map((value) => value.trim());

        if (values.length === 1 && values[0] === '') {
            setPasteSummary(null);
            return;
        }

        const parsedValues = values.map((value) => parseNumericText(value, null));
        const invalidCount = values.filter((value, index) => value !== '' && parsedValues[index] === null).length;

        setRows((prevRows) => prevRows.map((row, index) => (
            index < parsedValues.length ? { ...row, valor: parsedValues[index] } : row
        )));
        setPasteSummary({ total: values.length, invalidCount });
    }

    function getPasteHelperText () {
        if (!pasteSummary) {
            return `Cole os ${rows.length} valores na ordem das empresas da tabela, um por linha.`;
        }

        const messages = [`${pasteSummary.total} valores colados para ${rows.length} empresas.`];

        if (pasteSummary.total !== rows.length) {
            messages.push('A quantidade não confere com a tabela.');
        }

        if (pasteSummary.invalidCount > 0) {
            messages.push(`${pasteSummary.invalidCount} valor(es) inválido(s) ficaram vazios.`);
        }

        return messages.join(' ');
    }

    const hasPasteWarning = Boolean(pasteSummary) && (
        pasteSummary.total !== rows.length || pasteSummary.invalidCount > 0
    );

    async function submitLancamentoSPE(e) {
        e.preventDefault();
        setIsSubmit(true);

        let periodo = '';
        if (dateTimePeriodo) {
            setErrorPeriodo(false);
            const ano = dateTimePeriodo.getFullYear();
            const mes = dateTimePeriodo.getMonth() + 1;
            periodo = ano + '-' + mes;
        } else {
            setErrorPeriodo(true);
            setIsSubmit(false);
            return;
        }

         const spes = rows.filter(row => row.valor !== null && row.valor > 0);

        const body = JSON.stringify({
            spes,
            periodo
        });

        const response = await fetchFlask('lancamentoSPE', body);
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
        setIsSubmit(false);
    };

    return (
        <Box className='mainBox'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Lancamentos SPEs
            </PageHeader>

            <form action='' method='POST' id='formAcordo' onSubmit={submitLancamentoSPE}>
                <Grid container>
                    <Grid item xs={10}>
                        <Paper elevation={2} sx={{ padding: 4, marginTop: '20px', width: '100%' }}>
                            <Grid container spacing={2} justifyContent='center'>
                                <FormControl sx={{ marginTop: '20px' }}>
                                    <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                        <DatePicker id='data' label='Projeto'
                                            views={['month', 'year']}
                                            openTo='month'
                                            minDate={new Date('2023-01-02')}
                                            maxDate={new Date()}
                                            value={dateTimePeriodo}
                                            onChange={(value) => setDateTimePeriodo(value)}
                                            slotProps={{
                                                openPickerButton: {
                                                    color: 'primary'
                                                }
                                            }}
                                        />
                                        {errorPeriodo && (
                                            <Typography variant='caption' color='red'>
                                                Por favor selecione uma data!
                                            </Typography>
                                        )}
                                    </LocalizationProvider>
                                </FormControl>
                                <Grid item xs={12}>
                                    <TextField
                                        label='Colar valores do Excel'
                                        placeholder='Cole aqui a coluna de valores copiada do Excel.'
                                        value={valoresPaste}
                                        onChange={(e) => setValoresPaste(e.target.value)}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const text = e.clipboardData.getData('text');
                                            setValoresPaste(text);
                                            parseValoresPaste(text);
                                        }}
                                        multiline
                                        minRows={4}
                                        maxRows={8}
                                        fullWidth
                                        error={hasPasteWarning}
                                        helperText={getPasteHelperText()}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box>
                                        <DataGrid
                                            rows={rows}
                                            columns={columns}
                                            hideFooter
                                            autoHeight
                                            processRowUpdate={processRowUpdate}
                                            experimentalFeatures={{ newEditingApi: true }}
                                            initialState={{
                                                columns: {
                                                    columnVisibilityModel: {
                                                        id: false
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <FormControl>
                                            <SubmitButton isSubmit={isSubmit}>Lançamento SPEs</SubmitButton>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </form >
        </Box >
    );
};

export default LancamentoSPE;
